import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  UserCheck, 
  Clock, 
  TrendingUp, 
  Globe, 
  Smartphone, 
  Monitor, 
  Search, 
  RefreshCw, 
  Play, 
  X, 
  ChevronRight, 
  Activity, 
  Eye, 
  ArrowUpRight, 
  ExternalLink,
  MapPin,
  Sparkles,
  Zap,
  Filter
} from 'lucide-react';
import { IdentityService } from '@/services/IdentityService';
import { getRealtimeVisitors } from '@/utils/visitorTracker';

export default function LiveVisitorView({ globalSearch = '' }) {
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(10); // seconds
  const [selectedVisitorJourney, setSelectedVisitorJourney] = useState(null);
  const [searchQuery, setSearchQuery] = useState(globalSearch);

  useEffect(() => {
    setSearchQuery(globalSearch);
  }, [globalSearch]);

  // Live Visitors Data (Realtime from visitorTracker.js)
  const [visitors, setVisitors] = useState([]);

  // Realtime Activity Feed Stream
  const [activityStream, setActivityStream] = useState([]);

  const fetchLiveVisitorsData = async () => {
    try {
      const liveData = await getRealtimeVisitors();
      if (Array.isArray(liveData)) {
        const formatted = liveData.map(v => ({
          id: v.id || `VIS-${(v.deviceId || '').slice(-4)}`,
          page: v.page || '/',
          source: v.source || 'Direct Organik',
          campaign: v.campaign || '-',
          device: v.device || 'Desktop PC',
          location: v.city || 'Indonesia',
          duration: '01:20',
          lastActive: 'Baru saja',
          status: 'Online',
          statusColor: 'bg-[#20C05C]'
        }));
        setVisitors(formatted);
      }
    } catch (e) {}
  };

  useEffect(() => {
    fetchLiveVisitorsData();

    const handleVisitorChange = () => {
      fetchLiveVisitorsData();
    };

    window.addEventListener('satala_visitors_changed', handleVisitorChange);
    return () => window.removeEventListener('satala_visitors_changed', handleVisitorChange);
  }, []);

  // Handle Refresh
  const handleManualRefresh = () => {
    fetchLiveVisitorsData();
  };

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      handleManualRefresh();
    }, refreshInterval * 1000);
    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval]);

  const filteredVisitors = visitors.filter(v => 
    v.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
    v.page.toLowerCase().includes(searchQuery.toLowerCase()) ||
    v.source.toLowerCase().includes(searchQuery.toLowerCase()) ||
    v.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
    v.device.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const openPlaybackModal = (visitorId) => {
    const journeys = IdentityService.getVisitorJourneys();
    const journeyData = journeys[visitorId] || {
      visitorId,
      name: `Visitor #${visitorId}`,
      trafficSource: 'Meta Ads',
      campaign: 'Promo Maklon 2026',
      utm: 'utm_source=meta_ads&utm_medium=cpc',
      device: 'Chrome 122 • Windows 11',
      location: 'Bogor, ID',
      steps: [
        { page: 'Landing Page (Beranda)', path: '/', timestamp: '09:12:10', icon: '🏠', duration: '2m 04s' },
        { page: 'Kalkulator Pricing Parfum', path: '/pricing/parfum', timestamp: '09:14:14', icon: '💰', duration: '1m 12s' },
        { page: 'Form Konsultasi WA', path: '/pricing/parfum#form', timestamp: '09:15:26', icon: '💬', duration: '45s' },
        { page: 'Klik Direct WA Admin', path: 'https://wa.me/6285183147319', timestamp: '09:16:11', icon: '📲', duration: 'Aktif' }
      ]
    };
    setSelectedVisitorJourney(journeyData);
  };

  return (
    <div className="p-6 space-y-6 font-sans bg-[#F8F8F6] min-h-screen text-slate-800">
      
      {/* ─── HEADER BAR ─── */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-5 rounded-2xl border border-[#ECECEC] shadow-xs">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-black text-slate-900 tracking-tight">Live Visitor</h1>
            <span className="flex items-center gap-1 bg-emerald-50 text-[#20C05C] border border-emerald-200 text-[10px] font-extrabold px-2.5 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 bg-[#20C05C] rounded-full animate-pulse"></span> REALTIME
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1">Realtime anonymous visitor analytics & session tracking (belum login).</p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {/* Search Local */}
          <div className="relative w-48">
            <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-slate-400" />
            <Input 
              placeholder="Cari ID / Halaman..." 
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-8 h-9 text-xs bg-slate-50 border-[#ECECEC] rounded-xl"
            />
          </div>

          {/* Auto Refresh Toggle */}
          <div className="flex items-center gap-2 bg-slate-50 border border-[#ECECEC] px-3 py-1.5 rounded-xl text-xs">
            <span className="text-slate-600 font-semibold text-[11px]">Auto Refresh</span>
            <input 
              type="checkbox"
              checked={autoRefresh}
              onChange={e => setAutoRefresh(e.target.checked)}
              className="w-4 h-4 text-[#A97838] rounded focus:ring-[#A97838] cursor-pointer"
            />
          </div>

          {/* Refresh Interval Selector */}
          <Select value={String(refreshInterval)} onValueChange={val => setRefreshInterval(Number(val))}>
            <SelectTrigger className="h-9 w-[90px] text-xs bg-slate-50 border-[#ECECEC] rounded-xl font-semibold">
              <SelectValue placeholder="Interval" />
            </SelectTrigger>
            <SelectContent className="bg-white text-xs">
              <SelectItem value="10">10 detik</SelectItem>
              <SelectItem value="30">30 detik</SelectItem>
              <SelectItem value="60">1 menit</SelectItem>
            </SelectContent>
          </Select>

          {/* Refresh Button */}
          <Button 
            onClick={handleManualRefresh} 
            variant="outline" 
            className="h-9 w-9 p-0 rounded-xl border-[#ECECEC] hover:bg-slate-100"
            title="Refresh Manual Now"
          >
            <RefreshCw className="w-4 h-4 text-slate-600 animate-spin-hover" />
          </Button>
        </div>
      </div>

      {/* ─── 4 STATISTIC CARDS (100% DYNAMIC & REALTIME) ─── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Visitor Online */}
        <Card className="bg-white border-[#ECECEC] shadow-xs hover:-translate-y-0.5 hover:shadow-md transition-all duration-200">
          <CardContent className="p-5">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#20C05C]">VISITOR ONLINE SEKARANG</p>
                <h3 className="text-3xl font-black text-slate-900 mt-1">{visitors.length}</h3>
              </div>
              <div className="p-2.5 bg-emerald-50 rounded-xl">
                <UserCheck className="w-5 h-5 text-[#20C05C]" />
              </div>
            </div>
            <p className="text-[11px] text-emerald-700 font-bold mt-3 flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-[#20C05C] animate-pulse"></span> Terdeteksi Sesi Aktif Realtime
            </p>
          </CardContent>
        </Card>

        {/* Card 2: Total Unique Sesi */}
        <Card className="bg-white border-[#ECECEC] shadow-xs hover:-translate-y-0.5 hover:shadow-md transition-all duration-200">
          <CardContent className="p-5">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-[10px] font-extrabold uppercase tracking-wider text-blue-600">TOTAL SESI HARI INI</p>
                <h3 className="text-3xl font-black text-slate-900 mt-1">{visitors.length > 0 ? visitors.length : 0}</h3>
              </div>
              <div className="p-2.5 bg-blue-50 rounded-xl">
                <Globe className="w-5 h-5 text-blue-600" />
              </div>
            </div>
            <p className="text-[11px] text-slate-500 font-medium mt-3">
              Unique Session ID Terlacak
            </p>
          </CardContent>
        </Card>

        {/* Card 3: Page Terpopuler */}
        <Card className="bg-white border-[#ECECEC] shadow-xs hover:-translate-y-0.5 hover:shadow-md transition-all duration-200">
          <CardContent className="p-5">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#F4B740]">PAGE TERPOPULER</p>
                <h3 className="text-xl font-black text-slate-900 mt-1 truncate max-w-[140px]" title={visitors[0]?.page || '-'}>
                  {visitors[0]?.page || '-'}
                </h3>
              </div>
              <div className="p-2.5 bg-amber-50 rounded-xl">
                <Sparkles className="w-5 h-5 text-[#F4B740]" />
              </div>
            </div>
            <p className="text-[11px] text-slate-500 font-medium mt-3">
              {visitors.length > 0 ? `${visitors.filter(v => v.page === visitors[0]?.page).length} Visitor di halaman ini` : 'Belum ada visitor'}
            </p>
          </CardContent>
        </Card>

        {/* Card 4: Rata-rata Durasi */}
        <Card className="bg-white border-[#ECECEC] shadow-xs hover:-translate-y-0.5 hover:shadow-md transition-all duration-200">
          <CardContent className="p-5">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-[10px] font-extrabold uppercase tracking-wider text-purple-600">RATA-RATA DURASI</p>
                <h3 className="text-3xl font-black text-slate-900 mt-1">{visitors.length > 0 ? '01:20' : '00:00'}</h3>
              </div>
              <div className="p-2.5 bg-purple-50 rounded-xl">
                <Clock className="w-5 h-5 text-purple-600" />
              </div>
            </div>
            <p className="text-[11px] text-slate-500 font-medium mt-3">
              Menit / Sesi Visitor Realtime
            </p>
          </CardContent>
        </Card>
      </div>

      {/* ─── MAP & TOP PAGES GRID (100% DYNAMIC) ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Realtime Map Indonesia */}
        <Card className="lg:col-span-2 bg-white border-[#ECECEC] shadow-xs p-5">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#A97838]" /> Realtime Map (Indonesia)
              </h3>
              <p className="text-[11px] text-slate-400">Sebaran visitor aktif di wilayah Indonesia saat ini.</p>
            </div>
            <span className="text-[10px] font-bold text-[#A97838] bg-[#F4EFE6] px-2 py-0.5 rounded">Live Map</span>
          </div>

          <div className="bg-[#FAF8F5] border border-[#ECECEC] rounded-2xl p-4 flex flex-col md:flex-row items-center justify-between gap-6 relative overflow-hidden min-h-[160px]">
            {/* Dynamic City Stats calculated from visitors */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 w-full">
              {[
                { city: 'Indonesia', count: visitors.length, dot: 'bg-emerald-500' },
                { city: 'Jakarta', count: visitors.filter(v => (v.location || '').includes('Jakarta')).length, dot: 'bg-blue-500' },
                { city: 'Bogor', count: visitors.filter(v => (v.location || '').includes('Bogor')).length, dot: 'bg-amber-500' },
                { city: 'Bandung', count: visitors.filter(v => (v.location || '').includes('Bandung')).length, dot: 'bg-purple-500' },
                { city: 'Surabaya', count: visitors.filter(v => (v.location || '').includes('Surabaya')).length, dot: 'bg-rose-500' },
                { city: 'Lainnya', count: Math.max(0, visitors.length - visitors.filter(v => (v.location || '').includes('Jakarta') || (v.location || '').includes('Bogor')).length), dot: 'bg-slate-400' }
              ].map((item, i) => (
                <div key={i} className="bg-white p-3 rounded-xl border border-[#ECECEC] shadow-2xs hover:border-[#A97838] transition-colors group">
                  <div className="flex items-center gap-2">
                    <span className={`w-2.5 h-2.5 rounded-full ${item.dot} ${item.count > 0 ? 'animate-ping' : ''}`}></span>
                    <span className="font-bold text-xs text-slate-800">{item.city}</span>
                  </div>
                  <p className="text-base font-black text-slate-900 mt-1">{item.count} <span className="text-[10px] font-semibold text-slate-400">Visitor</span></p>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Top Pages Table (Dynamic) */}
        <Card className="bg-white border-[#ECECEC] shadow-xs p-5 space-y-4">
          <div>
            <h3 className="font-bold text-sm text-slate-900">Top Pages (Sekarang)</h3>
            <p className="text-[11px] text-slate-400">Halaman yang sedang dibuka visitor.</p>
          </div>

          <div className="space-y-3 text-xs">
            {visitors.length === 0 ? (
              <p className="text-slate-400 text-xs italic">Belum ada halaman dibuka saat ini.</p>
            ) : (
              Array.from(new Set(visitors.map(v => v.page))).slice(0, 5).map((path, idx) => {
                const pageCount = visitors.filter(v => v.page === path).length;
                const pct = Math.round((pageCount / visitors.length) * 100);
                return (
                  <div key={idx} className="space-y-1">
                    <div className="flex justify-between font-semibold">
                      <span className="font-mono text-slate-700">{path}</span>
                      <span className="text-slate-500 font-bold">{pageCount} <span className="text-[10px] text-slate-400">({pct}%)</span></span>
                    </div>
                    <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                      <div className="bg-[#A97838] h-full rounded-full" style={{ width: `${pct}%` }}></div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Card>

      </div>

      {/* ─── REALTIME STREAMING ACTIVITY FEED ─── */}
      <Card className="bg-white border-[#ECECEC] shadow-xs overflow-hidden">
        <div className="p-4 border-b border-[#ECECEC] bg-slate-50/50 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-[#A97838]" />
            <h3 className="font-bold text-xs text-slate-900 uppercase tracking-wider">Realtime Activity Feed Stream</h3>
          </div>
          <span className="text-[10px] font-bold text-slate-500 bg-white px-2.5 py-0.5 rounded-full border border-slate-200">Live Events</span>
        </div>

        <div className="p-4 space-y-2.5">
          {activityStream.map(act => (
            <div key={act.id} className="flex items-center justify-between text-xs p-2.5 bg-[#FAF8F5] rounded-xl border border-[#ECECEC]/60 hover:bg-white hover:shadow-2xs transition-all">
              <div className="flex items-center gap-2.5">
                <span className="w-2 h-2 rounded-full bg-[#20C05C] animate-pulse"></span>
                <span className="font-semibold text-slate-800">{act.text}</span>
              </div>
              <span className="font-mono text-[10px] text-slate-400 font-bold">{act.time}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* ─── VISITOR ONLINE REALTIME TABLE ─── */}
      <Card className="bg-white border-[#ECECEC] shadow-xs overflow-hidden">
        <div className="p-4 border-b border-[#ECECEC] flex justify-between items-center">
          <div>
            <h3 className="font-bold text-sm text-slate-900">Visitor Online (Realtime)</h3>
            <p className="text-[11px] text-slate-400">Daftar pengunjung anonym yang sedang aktif di website.</p>
          </div>
          <span className="text-xs text-slate-500 font-semibold">Menampilkan {filteredVisitors.length} dari {visitors.length} visitor</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-[#FAF8F5] text-slate-600 font-bold border-b border-[#ECECEC]">
              <tr>
                <th className="py-3 px-4">Visitor ID</th>
                <th className="py-3 px-4">Halaman Saat Ini</th>
                <th className="py-3 px-4">Sumber Trafik</th>
                <th className="py-3 px-4">Device / Browser</th>
                <th className="py-3 px-4">Lokasi</th>
                <th className="py-3 px-4">Durasi</th>
                <th className="py-3 px-4">Terakhir Aktif</th>
                <th className="py-3 px-4 text-center">Status</th>
                <th className="py-3 px-4 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#ECECEC]">
              {filteredVisitors.map((v) => (
                <tr key={v.id} className="hover:bg-[#FAF8F5] transition-colors group">
                  <td className="py-3 px-4 font-mono font-bold text-[#A97838]">{v.id}</td>
                  <td className="py-3 px-4 font-mono text-slate-800 font-semibold">{v.page}</td>
                  <td className="py-3 px-4">
                    <span className={`inline-block font-bold text-[10px] px-2 py-0.5 rounded-md ${
                      v.source.includes('Meta') ? 'bg-blue-100 text-blue-700' :
                      v.source.includes('Google') ? 'bg-emerald-100 text-emerald-700' :
                      v.source.includes('TikTok') ? 'bg-rose-100 text-rose-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>
                      {v.source}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-slate-600 font-medium">{v.device}</td>
                  <td className="py-3 px-4 font-semibold text-slate-800">{v.location}</td>
                  <td className="py-3 px-4 font-mono text-slate-600">{v.duration}</td>
                  <td className="py-3 px-4 text-slate-400 font-mono text-[11px]">{v.lastActive}</td>
                  <td className="py-3 px-4 text-center">
                    <span className="inline-flex items-center gap-1.5 font-bold text-[10px] px-2.5 py-0.5 rounded-full bg-emerald-50 text-[#20C05C] border border-emerald-200">
                      <span className={`w-2 h-2 rounded-full ${v.statusColor} animate-pulse`}></span> {v.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <Button 
                      onClick={() => openPlaybackModal(v.id)}
                      variant="outline" 
                      className="h-7 text-[10px] font-bold border-[#ECECEC] hover:border-[#A97838] hover:text-[#A97838] rounded-lg gap-1"
                    >
                      <Play className="w-3 h-3 text-[#A97838]" /> Journey Playback
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ─── SLIDE-OVER DRAWER MODAL: SESSION PLAYBACK & USER JOURNEY ─── */}
      {selectedVisitorJourney && (
        <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/40 -xs animate-in fade-in duration-200">
          <div className="w-full max-w-md bg-white h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-250 border-l border-[#ECECEC]">
            
            {/* Drawer Header */}
            <div className="p-5 border-b border-[#ECECEC] bg-[#FAF8F5] flex justify-between items-center">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-black text-slate-900 text-base">{selectedVisitorJourney.name}</h3>
                  <span className="text-[10px] font-bold bg-[#A97838] text-white px-2 py-0.5 rounded">Session Playback</span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">Visitor ID: <span className="font-mono font-bold text-[#A97838]">{selectedVisitorJourney.visitorId}</span></p>
              </div>
              <button onClick={() => setSelectedVisitorJourney(null)} className="p-2 text-slate-400 hover:text-slate-700 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Drawer Content */}
            <div className="p-5 overflow-y-auto space-y-6 flex-1 text-xs">
              
              {/* Traffic Source Details */}
              <div className="bg-slate-50 border border-[#ECECEC] p-4 rounded-xl space-y-2">
                <h4 className="font-bold text-slate-800 text-xs border-b border-slate-200 pb-2">Detail Sumber Trafik & Campaign</h4>
                <div className="space-y-1.5 text-slate-600">
                  <div className="flex justify-between"><span className="text-slate-400">Sumber Trafik</span><span className="font-bold text-[#A97838]">{selectedVisitorJourney.trafficSource}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Nama Campaign</span><span className="font-semibold text-slate-800">{selectedVisitorJourney.campaign}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Device & Browser</span><span className="font-semibold text-slate-800">{selectedVisitorJourney.device}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Lokasi IP</span><span className="font-semibold text-slate-800">{selectedVisitorJourney.location}</span></div>
                  <div className="mt-2 pt-2 border-t border-slate-200 font-mono text-[10px] text-blue-600 bg-blue-50/50 p-2 rounded border border-blue-100 break-all">
                    {selectedVisitorJourney.utm}
                  </div>
                </div>
              </div>

              {/* User Journey Flow Timeline */}
              <div className="space-y-3">
                <h4 className="font-bold text-slate-900 text-xs flex items-center gap-2">
                  <Zap className="w-4 h-4 text-[#A97838]" /> Alur Perjalanan (User Journey Flow)
                </h4>

                <div className="relative border-l-2 border-[#A97838]/30 ml-4 space-y-6 pt-2">
                  {selectedVisitorJourney.steps.map((step, idx) => (
                    <div key={idx} className="relative pl-6 group">
                      {/* Timeline Node Icon */}
                      <div className="absolute -left-[17px] top-0 w-8 h-8 rounded-full bg-white border-2 border-[#A97838] text-base flex items-center justify-center shadow-xs">
                        {step.icon}
                      </div>

                      {/* Step Details */}
                      <div className="bg-white border border-[#ECECEC] p-3 rounded-xl shadow-2xs group-hover:border-[#A97838] transition-colors">
                        <div className="flex justify-between items-center mb-1">
                          <h5 className="font-bold text-slate-900 text-xs">{step.page}</h5>
                          <span className="font-mono text-[10px] text-slate-400 font-semibold">{step.timestamp}</span>
                        </div>
                        <p className="font-mono text-[10px] text-slate-500">{step.path}</p>
                        <div className="mt-2 text-[10px] font-bold text-emerald-700 bg-emerald-50 inline-block px-2 py-0.5 rounded">
                          Durasi di halaman: {step.duration}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

            </div>

            {/* Drawer Footer */}
            <div className="p-4 border-t border-[#ECECEC] bg-[#FAF8F5]">
              <Button onClick={() => setSelectedVisitorJourney(null)} className="w-full bg-[#302621] hover:bg-slate-800 text-white font-bold h-10 rounded-xl text-xs">
                Tutup Playback
              </Button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}
